import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
  Easing,
} from 'remotion';
import { loadFont as loadMontserrat } from '@remotion/google-fonts/MontserratAlternates';
import { loadFont as loadNoto } from '@remotion/google-fonts/NotoSansDevanagari';
import { BadrinathSilhouette } from './BadrinathSilhouette';
import type { MantraCardData } from './types';

const { fontFamily: mont } = loadMontserrat('normal', { weights: ['900'], subsets: ['latin'] });
const { fontFamily: noto } = loadNoto('normal', { weights: ['700'], subsets: ['devanagari'] });

const FONT_SIZE = 88;


// ── Lotus ─────────────────────────────────────────────────────────────────────
const Lotus: React.FC<{ cx: number; cy: number; r: number; startFrame: number; uid: string }> = ({ cx, cy, r, startFrame, uid }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const relFrame = frame - startFrame;
  if (relFrame < 0) return null;
  const sc = spring({ fps, frame: Math.max(0, relFrame), config: { damping: 13, stiffness: 110, mass: 0.8 }, durationInFrames: 18 });
  const pulse = 1 + 0.035 * Math.sin(frame * 0.18);
  return (
    <g transform={`translate(${cx}, ${cy}) scale(${sc * pulse})`}>
      <defs>
        <linearGradient id={`lg-${uid}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#FFFFF0" /><stop offset="45%" stopColor="#FFD700" /><stop offset="100%" stopColor="#C8860A" />
        </linearGradient>
        <filter id={`lf-${uid}`} x="-40%" y="-40%" width="180%" height="180%">
          <feGaussianBlur stdDeviation="3" result="b" /><feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
      </defs>
      {Array.from({ length: 8 }).map((_, i) => (
        <ellipse key={i} cx={0} cy={-r * 0.55} rx={r * 0.24} ry={r * 0.6}
          fill={`url(#lg-${uid})`} stroke="rgba(255,200,0,0.4)" strokeWidth={0.5} opacity={0.88}
          transform={`rotate(${i * 45})`} />
      ))}
      {Array.from({ length: 4 }).map((_, i) => (
        <ellipse key={i} cx={0} cy={-r * 0.22} rx={r * 0.17} ry={r * 0.32}
          fill="#FFFDE0" opacity={0.92} transform={`rotate(${i * 90 + 22.5})`} />
      ))}
      <circle cx={0} cy={0} r={r * 0.12} fill="#FFF8C0" filter={`url(#lf-${uid})`} />
    </g>
  );
};

// ── Chakra ────────────────────────────────────────────────────────────────────
const Chakra: React.FC<{ cx: number; cy: number; r: number; startFrame: number; uid: string }> = ({ cx, cy, r, startFrame, uid }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const relFrame = frame - startFrame;
  if (relFrame < 0) return null;
  const sc = spring({ fps, frame: Math.max(0, relFrame), config: { damping: 180 }, durationInFrames: 28 });
  const rotation = frame * 3;
  const pulse = 1 + 0.025 * Math.sin(frame * 0.22);
  return (
    <g transform={`translate(${cx}, ${cy}) scale(${sc * pulse}) scale(1, 0.6)`}>
      <defs>
        <linearGradient id={`cg-${uid}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#FFF8C0" /><stop offset="100%" stopColor="#D4A030" />
        </linearGradient>
        <filter id={`cf-${uid}`} x="-30%" y="-30%" width="160%" height="160%">
          <feGaussianBlur stdDeviation="6" result="b" /><feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
      </defs>
      <g transform={`rotate(${rotation})`} filter={`url(#cf-${uid})`}>
        <circle cx={0} cy={0} r={r} fill="none" stroke="#FFD700" strokeWidth={r * 0.018} opacity={0.55} />
        <circle cx={0} cy={0} r={r * 0.78} fill="none" stroke="#FFD700" strokeWidth={r * 0.045} strokeDasharray={`${r * 0.13} ${r * 0.065}`} />
        <circle cx={0} cy={0} r={r * 0.56} fill="none" stroke="#FFD700" strokeWidth={r * 0.018} />
        {Array.from({ length: 16 }).map((_, i) => (
          <polygon key={i} points={`-${r * 0.045},-${r * 0.56} ${r * 0.045},-${r * 0.56} 0,-${r * 0.9}`}
            fill={`url(#cg-${uid})`} transform={`rotate(${i * 22.5})`} />
        ))}
        <circle cx={0} cy={0} r={r * 0.14} fill="#FFD700" opacity={0.95} />
      </g>
    </g>
  );
};

// ── Sparkle ───────────────────────────────────────────────────────────────────
const Sparkle: React.FC<{ cx: number; cy: number; r: number; phase: number }> = ({ cx, cy, r, phase }) => {
  const frame = useCurrentFrame();
  const t = (frame * 1.4 + phase) % 64;
  const op = interpolate(t, [0, 10, 32, 54, 64], [0, 0.9, 0.4, 0.9, 0], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });
  const sc = interpolate(t, [0, 10, 32, 54, 64], [0.5, 1.1, 0.8, 1.1, 0.5], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });
  const s = r * sc;
  return (
    <polygon
      points={`${cx},${cy - s * 2.2} ${cx + s * 0.45},${cy - s * 0.45} ${cx + s * 2.2},${cy} ${cx + s * 0.45},${cy + s * 0.45} ${cx},${cy + s * 2.2} ${cx - s * 0.45},${cy + s * 0.45} ${cx - s * 2.2},${cy} ${cx - s * 0.45},${cy - s * 0.45}`}
      fill="#FFF8C0" opacity={op} />
  );
};


// ── Main ──────────────────────────────────────────────────────────────────────
export const MantraCardBadrinath1: React.FC<{
  data: MantraCardData;
  durationInFrames: number;
}> = ({ data, durationInFrames }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const fadeOutStart = durationInFrames - Math.round(fps * 1.5);
  const globalOpacity = interpolate(frame, [fadeOutStart, durationInFrames - 1], [1, 0], {
    extrapolateLeft: 'clamp', extrapolateRight: 'clamp',
  });

  const jayStart   = Math.round(((data.words[0]?.offset_ms ?? 0)   / 1000) * fps);
  const badriStart = Math.round(((data.words[1]?.offset_ms ?? 302) / 1000) * fps);
  // VISHAL uses same gap as JAI→BADRI, ignoring the long audio pause in offset_ms
  const gap        = badriStart - jayStart;
  const vishalStart = badriStart + gap;

  const WIPE = 16;

  const eaz = Easing.bezier(0.4, 0, 0.2, 1);
  const jayClip    = interpolate(frame, [jayStart,    jayStart    + WIPE], [0, 100], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp', easing: eaz });
  const badriClip  = interpolate(frame, [badriStart,  badriStart  + WIPE], [0, 100], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp', easing: eaz });
  const vishalClip = interpolate(frame, [vishalStart, vishalStart + WIPE], [0, 100], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp', easing: eaz });

  const lineW   = interpolate(frame, [badriStart + WIPE, badriStart + WIPE + 14], [0, 100], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp', easing: eaz });
  const tempOp  = interpolate(frame, [jayStart, jayStart + 10], [0, 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });
  const hindiOp = interpolate(frame, [vishalStart + WIPE, vishalStart + WIPE + 14], [0, 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });

  const decoStart   = badriStart + WIPE + 10;
  const lotusFrame  = decoStart;
  const chakraFrame = decoStart + 8;

  const shadow = '0 0 60px rgba(255,215,0,0.25)';
  const textStyle: React.CSSProperties = {
    fontSize: FONT_SIZE,
    fontWeight: 900,
    fontFamily: mont,
    color: '#fff',
    letterSpacing: 12,
    lineHeight: 1,
    textShadow: shadow,
  };

  return (
    <AbsoluteFill style={{ opacity: globalOpacity, backgroundColor: 'transparent' }}>

      {/* Centred column */}
      <AbsoluteFill style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0 }}>

        {/* Badrinath temple silhouette */}
        <div style={{ opacity: tempOp }}>
          <BadrinathSilhouette w={150} h={165} />
        </div>

        {/* JAI  BADRI  VISHAL — same line, same font */}
        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'baseline', gap: 28, marginTop: 18 }}>
          <div style={{ overflow: 'hidden' }}>
            <div style={{ ...textStyle, clipPath: `inset(0 ${100 - jayClip}% 0 0)` }}>JAI</div>
          </div>
          <div style={{ overflow: 'hidden' }}>
            <div style={{ ...textStyle, clipPath: `inset(0 ${100 - badriClip}% 0 0)` }}>BADRI</div>
          </div>
          <div style={{ overflow: 'hidden' }}>
            <div style={{ ...textStyle, clipPath: `inset(0 ${100 - vishalClip}% 0 0)` }}>VISHAL</div>
          </div>
        </div>

        {/* Gold underline */}
        <div style={{
          width: `${lineW * 7}px`, maxWidth: 860, height: 2,
          background: 'rgba(255,215,0,0.75)',
          marginTop: 10,
        }} />

        {/* Hindi subtitle */}
        <div style={{
          opacity: hindiOp,
          color: 'rgba(255,215,0,0.85)',
          fontSize: 42,
          fontFamily: noto,
          fontWeight: 700,
          letterSpacing: 0,
          marginTop: 12,
        }}>
          {data.words.map(w => w.word).join(' ')}
        </div>

        {/* Space for lotus/chakra below */}
        <div style={{ height: 100 }} />
      </AbsoluteFill>

      {/* SVG: Lotus + Chakra + Sparkles */}
      <AbsoluteFill style={{ pointerEvents: 'none' }}>
        <svg width={1280} height={704} viewBox="0 0 1280 704" xmlns="http://www.w3.org/2000/svg">
          <Sparkle cx={80}   cy={100} r={4} phase={0}  />
          <Sparkle cx={1200} cy={110} r={4} phase={20} />
          <Sparkle cx={90}   cy={580} r={3} phase={35} />
          <Sparkle cx={1190} cy={570} r={3} phase={12} />
          <Sparkle cx={400}  cy={80}  r={3} phase={8}  />
          <Sparkle cx={880}  cy={75}  r={3} phase={28} />

          {/* Lotus left · Chakra centre · Lotus right */}
          <Lotus       cx={490} cy={560} r={40} startFrame={lotusFrame}          uid="ll" />
          <Chakra      cx={640} cy={565} r={55} startFrame={chakraFrame}         uid="cc" />
          <Lotus       cx={790} cy={560} r={40} startFrame={lotusFrame + 6}      uid="lr" />
        </svg>
      </AbsoluteFill>

    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
